import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/base/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { getArticleById, getHotArticles } from "@/mocks/articles";
import { getCategory } from "@/mocks/categories";
import { formatHeat } from "@/utils/format";
import ArticleContent from "@/pages/article/components/ArticleContent";
import ArticleToc, { type TocItem } from "@/pages/article/components/ArticleToc";
import RelatedArticles from "@/pages/article/components/RelatedArticles";

export default function ArticlePage() {
  const { id = "" } = useParams();
  const article = getArticleById(id);

  if (!article) {
    return (
      <SiteLayout>
        <Seo
          title="瓜帖不存在 | 网红大瓜"
          description="你访问的瓜帖可能已被撤下或链接有误，返回首页查看最新猛料。"
          canonicalPath="/article"
        />
        <div className="mx-auto flex max-w-3xl flex-col items-center px-4 py-24 text-center md:py-32">
          <span className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="mb-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            这条瓜帖找不到了
          </h1>
          <p className="mb-8 text-sm text-foreground-600">
            可能是链接输错了，也可能是内容正在核对调整。先看看热门榜单吧。
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const category = getCategory(article.category);
  const hotList = getHotArticles(5).filter((item) => item.id !== article.id).slice(0, 4);

  const crumbs = [
    { label: "首页", path: "/" },
    { label: category?.name ?? "瓜帖", path: `/category/${article.category}` },
    { label: article.title.slice(0, 14), path: `/article/${article.id}` },
  ];

  const tocItems: TocItem[] = [
    { id: "overview", label: "事件概述" },
    { id: "screens", label: "关键截图" },
    ...article.sections.map((section, index) => ({
      id: `section-${index + 1}`,
      label: section.heading,
    })),
    { id: "timeline", label: "关键节点时间线" },
    { id: "followup", label: "后续进展" },
    { id: "related", label: "相关大瓜推荐" },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`${article.title} | 网红大瓜`}
        description={article.summary}
        keywords={[article.title.slice(0, 12), ...article.tags, "网红大瓜", "事件时间线"]}
        canonicalPath={`/article/${article.id}`}
        image={article.cover}
        imageAlt={article.coverAlt}
        breadcrumbs={crumbs}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.summary,
            image: [article.cover],
            datePublished: article.publishedAt,
            dateModified: article.updatedAt,
            author: { "@type": "Person", name: article.author },
            publisher: {
              "@type": "Organization",
              name: "网红大瓜",
              url: "https://chengshunjt.cn/",
            },
            articleSection: category?.name ?? "网红大瓜",
            keywords: article.tags.join(","),
            inLanguage: "zh-CN",
            mainEntityOfPage: `https://chengshunjt.cn/article/${article.id}`,
          },
        ]}
      />

      <article>
        <header className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-[1400px] px-4 pt-6 md:px-6 md:pt-8">
            <Breadcrumb items={crumbs} />
            <div className="grid grid-cols-1 gap-8 py-8 lg:grid-cols-12 lg:gap-12 lg:py-10">
              <div className="lg:col-span-7">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="inline-flex items-center rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                    >
                      {category.name}
                    </Link>
                  )}
                  <span className="inline-flex items-center gap-1 rounded-full border border-accent-300 bg-accent-100 px-3 py-1 text-xs text-accent-900">
                    <i className="ri-fire-fill" />
                    {formatHeat(article.heat)} 热度
                  </span>
                </div>
                <h1 className="mb-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                  {article.title}
                </h1>
                <p className="mb-6 text-sm leading-relaxed text-foreground-600 md:text-base">
                  {article.subtitle}
                </p>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-3-line" />
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line" />
                    发布于 {article.publishedAt}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-refresh-line" />
                    更新于 {article.updatedAt}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line" />
                    约 {article.readMinutes} 分钟读完
                  </span>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-64 lg:h-full lg:min-h-[260px]">
                  <img
                    src={article.cover}
                    alt={article.coverAlt}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-8">
              <ArticleContent article={article} />
              <div className="mt-10">
                <RelatedArticles article={article} />
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="sticky top-24 space-y-6">
                <ArticleToc items={tocItems} />

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="mb-4 flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                    <i className="ri-fire-fill text-primary-500" />
                    热门大瓜
                  </h2>
                  <ol className="divide-y divide-background-200">
                    {hotList.map((item, index) => (
                      <li key={item.id}>
                        <ArticleCard article={item} variant="compact" rank={index + 1} />
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="mb-2 flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                    <i className="ri-information-line text-primary-500" />
                    内容说明
                  </h2>
                  <p className="text-xs leading-relaxed text-foreground-600">
                    本文基于公开渠道信息整理，人物均为化名，仅用于呈现事件经过。如内容存在事实性错误或涉及你的个人信息，
                    可通过
                    <Link to="/contact" className="mx-1 text-primary-600 hover:underline">
                      联系我们
                    </Link>
                    提交说明，我们会尽快核对处理。
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>
    </SiteLayout>
  );
}